import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { UserCog, Plus, Trash2, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagementModal = ({ open, onClose }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente brugere');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newUser.username || !newUser.password) {
      toast.error('Udfyld alle felter');
      return;
    }

    setIsCreating(true);
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('Bruger oprettet!');
      setNewUser({ username: '', password: '', role: 'user' });
      setShowCreate(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette bruger');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Er du sikker på du vil slette brugeren "${username}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Bruger slettet');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette bruger');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="user-management-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Brugeradministration
          </DialogTitle>
          <DialogDescription>
            Opret og administrer brugere
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create New User */}
          {!showCreate ? (
            <Button
              onClick={() => setShowCreate(true)}
              className="w-full"
              data-testid="show-create-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Opret Ny Bruger
            </Button>
          ) : (
            <form onSubmit={handleCreate} className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <h4 className="font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Ny Bruger
              </h4>
              <div className="space-y-2">
                <Label>Brugernavn</Label>
                <Input
                  placeholder="Indtast brugernavn"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  data-testid="new-user-username"
                />
              </div>
              <div className="space-y-2">
                <Label>Adgangskode</Label>
                <Input
                  type="password"
                  placeholder="Indtast adgangskode"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  data-testid="new-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Rang</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({...newUser, role: value})}
                >
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Bruger</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreate(false)}
                  className="flex-1"
                >
                  Annuller
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1"
                  data-testid="create-user-btn"
                >
                  {isCreating ? 'Opretter...' : 'Opret'}
                </Button>
              </div>
            </form>
          )}

          <Separator />

          {/* User List */}
          <div className="space-y-2">
            <h4 className="font-medium">Eksisterende Brugere</h4>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`user-item-${user.username}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{user.username}</p>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                          {user.role === 'admin' ? 'Administrator' : 'Bruger'}
                        </Badge>
                      </div>
                    </div>
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user.id, user.username)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-user-${user.username}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementModal;
